import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ActionPlanStatus, DemandAnalysis, DemandAnalysisQuestionStatus, DemandAnalysisStatus } from '../api/schemas/schema'
import { Layout } from './Layout'
import { Loader } from '../components/common/spinner/Loader'
import { Button, SelectTabData, Tab } from '@fluentui/react-components'
import { t } from '../i18n/i18n'
import {
   BookInformationRegular,
   BookInformationFilled,
   TaskListSquareLtrFilled,
   TaskListSquareLtrRegular,
   ClipboardSearchFilled,
   ClipboardSearchRegular,
} from '@fluentui/react-icons'
import { handleMessage } from '../helpers/stateHelper'
import { LockClosed24Regular } from '@fluentui/react-icons'
import { DemandAssessmentTabs, filterQuestions, isDemandAssessmentOwnerOrCreator } from '../helpers/demandAssessmentHelper'
import { api, getDemandAnalysisQuestions } from '../helpers/apiHelper'
import { CurrentDemandAnalysis } from '../components/demandAnalysis/demandAnalysis/DemandAnalysis'
import { ActionPlans } from '../components/demandAnalysis/actionPlan/ActionPlans'
import { DemandAssessmentNavLinks } from '../components/common/navigation/DemandAssessmentNavLinks'
import { DataContext } from '../context/DataContext'
import { ModalCompleteDemandAssessment } from '../components/demandAnalysis/ModalCompleteDemandAssessment'
import { DemandAssesmentInformation } from '../components/demandAnalysis/information/DemandAssesmentInformation'
import { IItemState } from '../interfaces/IItemState'
import { NavigationLink } from '../interfaces/INavigationLink'
import { isSystemAdmin } from '../helpers/permissionHelper'

export const SelectedDemandAssessment = () => {
   const [analysisState, setAnalysisState] = useState<IItemState<DemandAnalysis>>({ disabled: true, loading: true, error: null, item: null })
   const [questionsLoading, setQuestionsLoading] = useState<boolean>(true)
   const { loading, error, item } = analysisState
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const { id } = useParams()
   const prevId = useRef({})
   const navigate = useNavigate()
   const [searchParams, setSearchParams] = useSearchParams()
   const selectedTab = searchParams.get('tab')
   const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false)

   useEffect(() => {
      const fetchData = async () => {
         const metadataPromise = api().getDemandAnalysisById(id, dataContext.state.organisation.id)
         const questionsPromise = getDemandAnalysisQuestions(id, dataContext.state.organisation.id)

         try {
            const demandAnalysis = (await metadataPromise).data
            setAnalysisState((prev) => ({ ...prev, loading: false, item: demandAnalysis, disabled: demandAnalysis.status === DemandAnalysisStatus.Completed }))
         } catch (error: any) {
            setAnalysisState((prev) => ({ ...prev, loading: false, error: { message: error?.error?.message, name: error?.status } }))
            navigate(NavigationLink.ComplianceOfDemands)
            handleMessage(dataContext.setRootState, 'error', t('FetchFailed'), t('CouldNotFetchItem', { Item: t('DemandAssessment').toLowerCase() }))
            return
         }

         try {
            const demandAnalysisWithQuestions = await questionsPromise
            setAnalysisState((prev) => ({
               ...prev,
               item: { ...prev.item, questions: demandAnalysisWithQuestions.questions, actionPlans: demandAnalysisWithQuestions.actionPlans },
            }))
            setQuestionsLoading(false)
         } catch (error: any) {
            setAnalysisState((prev) => ({ ...prev, loading: false, error: { message: error?.error?.message, name: error?.status } }))
            navigate(NavigationLink.ComplianceOfDemands)
            handleMessage(dataContext.setRootState, 'error', t('FetchFailed'), t('CouldNotFetchItem', { Item: t('DemandAssessment').toLowerCase() }))
         }
      }

      if (prevId.current !== id) {
         prevId.current = id
         fetchData()
      }

      if (analysisState.item && analysisState.item.organisationId !== dataContext.state.organisation.id) {
         navigate(NavigationLink.ComplianceOfDemands)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, dataContext.state.userAccount])

   useEffect(() => {
      if (searchParams.get('tab') === null) {
         setSearchParams((prev) => ({ ...prev, tab: DemandAssessmentTabs.Info }))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchParams])

   useEffect(() => {
      if (analysisState?.item) {
         if (!isDemandAssessmentOwnerOrCreator(analysisState.item, userAccount) && !isSystemAdmin(userAccount)) {
            setAnalysisState((prev) => ({ ...prev, disabled: true }))
         }
      }
   }, [analysisState, userAccount])

   const completeBtnDisabled =
      analysisState.disabled ||
      analysisState.loading ||
      questionsLoading ||
      !filterQuestions(t('AllProcesses'), analysisState.item?.questions ?? []).every((x) => x.status === DemandAnalysisQuestionStatus.Determined) ||
      !(analysisState.item?.actionPlans ?? []).every((x) => x.status === ActionPlanStatus.Decided || x.status === ActionPlanStatus.NotToBeAdressed)

   return (
      <Layout
         phoneNavLinks={
            <DemandAssessmentNavLinks
               id={analysisState?.item?.id ?? ''}
               active={selectedTab}
               completeDemandAnalysisTab={<Tab value={'complete'} icon={<LockClosed24Regular />} disabled={completeBtnDisabled} />}
            />
         }
         activeDesktopLink={NavigationLink.ComplianceOfDemands}
         activePhoneTab={`${NavigationLink.ComplianceOfDemands}/${analysisState?.item?.id}?tab=${selectedTab}`}
         customOnTabSelect={(e, data: SelectTabData) => {
            if (data.value === 'complete') setCompleteModalOpen(true)
            else navigate(data.value)
         }}
         breadCrumbItems={[
            { path: '/compliance', active: false, title: t('ComplianceOfDemands') },
            { path: `/compliance/${item?.id}`, active: true, title: item?.name },
         ]}
         button={
            item &&
            item.status === DemandAnalysisStatus.Ongoing && (
               <Button appearance="transparent" icon={<LockClosed24Regular />} onClick={() => setCompleteModalOpen(true)} disabled={completeBtnDisabled}>
                  {t('CompleteDemandAssessment')}
               </Button>
            )
         }
         tabs={
            <>
               <Tab value={DemandAssessmentTabs.Info} icon={selectedTab === DemandAssessmentTabs.Info ? <BookInformationFilled /> : <BookInformationRegular />}>
                  {t('Information')}
               </Tab>
               <Tab
                  value={DemandAssessmentTabs.DemandAnalysis}
                  icon={selectedTab === DemandAssessmentTabs.DemandAnalysis ? <ClipboardSearchFilled /> : <ClipboardSearchRegular />}
               >
                  {t('DemandAnalysis')}
               </Tab>
               {(isDemandAssessmentOwnerOrCreator(analysisState?.item, userAccount) || isSystemAdmin(userAccount)) && (
                  <Tab
                     value={DemandAssessmentTabs.ActionPlan}
                     icon={selectedTab === DemandAssessmentTabs.ActionPlan ? <TaskListSquareLtrFilled /> : <TaskListSquareLtrRegular />}
                  >
                     {t('ActionPlan')}
                  </Tab>
               )}
            </>
         }
         overflowAuto={true}
      >
         {loading ? (
            <Loader />
         ) : error ? (
            <>{t('CouldNotFetchItem', { Item: t('DemandAssessment').toLowerCase() })}</>
         ) : (
            <>
               {selectedTab === DemandAssessmentTabs.Info && <DemandAssesmentInformation analysisState={analysisState} setAnalysisState={setAnalysisState} />}
               {selectedTab === DemandAssessmentTabs.DemandAnalysis && (
                  <CurrentDemandAnalysis analysisState={analysisState} setAnalysisState={setAnalysisState} questionsLoading={questionsLoading} />
               )}

               {(isDemandAssessmentOwnerOrCreator(analysisState?.item, userAccount) || isSystemAdmin(userAccount)) &&
                  selectedTab === DemandAssessmentTabs.ActionPlan && <ActionPlans analysisState={analysisState} setAnalysisState={setAnalysisState} questionsLoading={questionsLoading} />}

               <ModalCompleteDemandAssessment
                  setAnalysisState={setAnalysisState}
                  analysisState={analysisState}
                  close={() => setCompleteModalOpen(false)}
                  open={completeModalOpen}
               />
            </>
         )}
      </Layout>
   )
}
